import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { ContentPagesState, ContentPagesStore } from './content-pages.store';
import { ContentPage } from './content-page.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ContentPagesQuery extends QueryEntity<ContentPagesState> {
  constructor(protected store: ContentPagesStore) {
    super(store);
  }

  selectLoading$ = this.select('loading');
  selectError$ = this.selectError();
  selectSearchQuery$ = this.select('searchQuery');
  selectFilterPublished$ = this.select('filterPublished');
  selectSortField$ = this.select('sortField');
  selectSortOrder$ = this.select('sortOrder');
  selectPagination$ = this.select('pagination');

  selectAllPages$ = this.selectAll();

  selectPublishedPages$ = this.selectAll({
    filterBy: page => page.isPublished,
  });

  selectDraftPages$ = this.selectAll({
    filterBy: page => !page.isPublished,
  });

  selectPageBySlug(slug: string): Observable<ContentPage | undefined> {
    return this.selectAll({
      filterBy: page => page.slug === slug,
    }).pipe(map(pages => pages[0]));
  }

  selectPageById(id: string): Observable<ContentPage | undefined> {
    return this.selectEntity(id);
  }

  selectTotalPages$ = this.select('pagination').pipe(
    map(pagination => pagination.totalPages)
  );

  selectCurrentPage$ = this.select('pagination').pipe(
    map(pagination => pagination.page)
  );

  selectPageSize$ = this.select('pagination').pipe(
    map(pagination => pagination.pageSize)
  );

  selectTotal$ = this.select('pagination').pipe(
    map(pagination => pagination.total)
  );

  get loading() {
    return this.getValue().loading;
  }

  get error() {
    return this.getValue().error;
  }

  get searchQuery() {
    return this.getValue().searchQuery;
  }

  get filterPublished() {
    return this.getValue().filterPublished;
  }

  get sortField() {
    return this.getValue().sortField;
  }

  get sortOrder() {
    return this.getValue().sortOrder;
  }

  get pagination() {
    return this.getValue().pagination;
  }

  get currentQueryParams() {
    const state = this.getValue();
    return {
      q: state.searchQuery || undefined,
      isPublished: state.filterPublished,
      sort: state.sortField ? `${state.sortField}:${state.sortOrder}` : undefined,
      page: state.pagination.page,
      pageSize: state.pagination.pageSize,
    };
  }
}